import { BaseComponent } from "../../../react-core/Html/BaseComponent";
import { TimeoutManager } from "../../../react-core/Helpers/TimerManager";
import { ExtendSessionStore, SessionInfoStore, SessionSettingStore } from "./Flux/Variables";
import * as React from "react";
import { SessionInfo } from "../../../Wrap/Models/Models/Session/SessionInfo";
import { ExtendSessionActions } from "./Flux/ExtendSessionActions";
import { SessionInfoActions } from "./Flux/SessionInfoActions";
import { TimeoutWillExpireLightbox } from "./TimeoutWillExpireLightbox";
import { SessionSettingActions } from "./Flux/SessionSettingActions";
import { UserStore } from "../../Dashboard/Stroes/Variable";
import { PortfolioDashboardAction } from "../../Dashboard/Components/Clients/PortfolioDashboard/Action/PortfolioDashboardAction";

export type ISessionTimeoutComponentProps = React.Props<SessionTimeoutComponent>;

export interface ISessionTimeoutComponentState {
    timeoutWillExpireLightboxIsOpen?: boolean;

    secondsToExpire?: number;
}

export class SessionTimeoutComponent extends BaseComponent<
    ISessionTimeoutComponentProps,
    ISessionTimeoutComponentState
> {
    private _defaultTimeoutInSeconds: number;

    private _secondsToShowMessageAheadOfLogout = 300;

    private _sessionCountdownTimer: TimeoutManager;

    private _logoutUrl: string;

    constructor(props: ISessionTimeoutComponentProps) {
        super(props);

        this.state = this.getDefaultState();

        this.subscribeToStore(ExtendSessionStore, () => this.onExtendSessionStoreChange());
        this.subscribeToStore(SessionInfoStore, () => this.onSessionInfoStoreChange());
        this.subscribeToStore(SessionSettingStore, () => this.onSessionSettingStoreStoreChange());
    }

    public componentDidMount(): void {
        this._sessionCountdownTimer = new TimeoutManager(() => this.onTimerExpired());
        if (!SessionSettingStore.getPayload()) {
            SessionSettingActions.get((result) => {
                if (result && result?.DefaultSessionExpiryPeriodInMins > 0) {
                    this._defaultTimeoutInSeconds = result.DefaultSessionExpiryPeriodInMins * 60;
                } else {
                    this._defaultTimeoutInSeconds = 1200;
                }
                this.setInitialTimerForMessage();
            });
        } else {
            this._defaultTimeoutInSeconds =
                SessionSettingStore.getPayload().DefaultSessionExpiryPeriodInMins > 0
                    ? SessionSettingStore.getPayload().DefaultSessionExpiryPeriodInMins * 60
                    : 1200;
            this.setInitialTimerForMessage();
        }
    }

    public render(): JSX.Element {
        return (
            <TimeoutWillExpireLightbox
                secondsToShowMessageAheadOfLogout={this._secondsToShowMessageAheadOfLogout}
                secondsLeft={this.state.secondsToExpire}
                onLogout={() => this.logout()}
                onTimerExpired={() => this.clearAccountSettingsAndLogout()}
                onKeepSignedIn={() => this.prolongSession()}
                show={this.state.timeoutWillExpireLightboxIsOpen}
            />
        );
    }

    private getDefaultState(): ISessionTimeoutComponentState {
        return {
            timeoutWillExpireLightboxIsOpen: false,
            secondsToExpire: 0,
        };
    }
    private setInitialTimerForMessage(): void {
        this.setState({ secondsToExpire: this._defaultTimeoutInSeconds });

        const timeout = this._defaultTimeoutInSeconds - this._secondsToShowMessageAheadOfLogout;
        this._sessionCountdownTimer.restart(timeout);
    }

    private onTimerExpired(): void {
        SessionInfoActions.get((result) => {
            this.handleExpiredTimer(result);
        });
    }

    private handleExpiredTimer(sessionInfo: SessionInfo): void {
        if (!this._secondsToShowMessageAheadOfLogout) {
            return;
        }
        if (this.isSessionExpired(sessionInfo)) {
            this.logoutOnSessionExpired();
            return;
        }

        if (this.shouldDisplayMessage(sessionInfo)) {
            this.setState({
                timeoutWillExpireLightboxIsOpen: true,
                secondsToExpire: sessionInfo.SecondsToExpire,
            });
            this._sessionCountdownTimer.restart(sessionInfo.SecondsToExpire);
        } else {
            this.setState({
                timeoutWillExpireLightboxIsOpen: false,
                secondsToExpire: sessionInfo.SecondsToExpire,
            });
            this._sessionCountdownTimer.restart(sessionInfo.SecondsToExpire - this._secondsToShowMessageAheadOfLogout);
        }
    }

    private prolongSession(): void {
        ExtendSessionActions.post(() => {
            SessionInfoActions.get((result) => {
                this.handleExpiredTimer(result);
            });
        });
    }

    private isSessionExpired(sessionInfo: SessionInfo): boolean {
        return !sessionInfo || !sessionInfo.IsValid || sessionInfo.SecondsToExpire <= 0;
    }

    private shouldDisplayMessage(sessionInfo: SessionInfo): boolean {
        return sessionInfo.SecondsToExpire <= this._secondsToShowMessageAheadOfLogout + 1;
    }

    private logout(): void {
        this._sessionCountdownTimer.stop();
        this.clearAccountSettingsAndLogout();
    }
    private logoutOnSessionExpired(): void {
        this._sessionCountdownTimer.stop();
        this.clearAccountSettingsAndLogout();
    }

    private sessionTerminatedRedirect(): void {
        window.location.assign("/accessdenied.aspx?logout=1");
    }

    private onExtendSessionStoreChange(): void {
        const sessionInfo = ExtendSessionStore.getPayload();
        if (sessionInfo) {
            this._logoutUrl = sessionInfo.LogoutUrl;
            this.handleExpiredTimer(sessionInfo);
        }
    }

    private onSessionInfoStoreChange(): void {
        const sessionInfo = SessionInfoStore.getPayload();
        if (sessionInfo) {
            this._logoutUrl = sessionInfo.LogoutUrl;
            this.handleExpiredTimer(sessionInfo);
        }
    }

    private onSessionSettingStoreStoreChange(): void {
        const sessionSetting = SessionSettingStore.getPayload();
        if (sessionSetting && sessionSetting?.DefaultSessionExpiryPeriodInMins > 0) {
            this._defaultTimeoutInSeconds = sessionSetting.DefaultSessionExpiryPeriodInMins * 60;
        } else {
            this._defaultTimeoutInSeconds = 1200;
        }
    }

    private clearAccountSettingsAndLogout() {
        PortfolioDashboardAction.deleteSettings((callBack) => {
            this.sessionTerminatedRedirect();
        });
    }
}
